import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchTaskReportsAsync } from "./taskReportsApi";
import { RootState } from "../../app/store";
import { MilestoneTaskReport } from "./MilestoneTaskReport";
import { MilestoneTask } from "./MilestoneTask";
import { SortConfig } from "./SortConfig";
import { TaskReportsState } from "./TaskReportState";
import TimeFormatter from "../extensions/TimeFormatter";

const initialState: TaskReportsState = {
  milestoneTask: [],
  initializationStatus: "not-initialized",
  status: "idle",
  pagination: {
    currentPage: 1,
    pageSize: 100,
    totalPages: 1,
    totalItems: 0,
  },
  sortConfig: {
    field: "dateRange",
    direction: "desc",
  },
  sortApplied: true,
};

export const formatDevelopers = (task: MilestoneTask): string => {
  if (!task.taskReports || task.taskReports.length === 0) {
    return "N/A";
  }

  const developerSet = new Set<string>();

  task.taskReports.forEach((report) => {
    if (report.developerName) {
      if (
        report.developerName.startsWith("[") &&
        report.developerName.endsWith("]")
      ) {
        try {
          const developers = JSON.parse(report.developerName);
          if (Array.isArray(developers)) {
            developers.forEach((dev) => developerSet.add(dev));
          } else {
            developerSet.add(report.developerName);
          }
        } catch (error) {
          developerSet.add(report.developerName);
        }
      } else {
        developerSet.add(report.developerName);
      }
    }
  });

  return Array.from(developerSet).join(", ") || "N/A";
};

export const getDeveloperTimeBreakdown = (
  task: MilestoneTask,
  useCommaAndSpace: boolean = false
) => {
  if (!task.taskReports || task.taskReports.length === 0) {
    return "No time data available";
  }

  const developerTimeMap = new Map<string, number>();

  task.taskReports.forEach((report) => {
    if (report.developerName) {
      let developers: string[] = [];

      try {
        const parsedDevelopers = JSON.parse(report.developerName);
        if (Array.isArray(parsedDevelopers)) {
          developers = parsedDevelopers;
        } else {
          developers = [report.developerName];
        }
      } catch (error) {
        developers = [report.developerName];
      }

      const timePerDeveloper = report.totalTimeSpentOnTask / developers.length;

      developers.forEach((dev) => {
        const currentTime = developerTimeMap.get(dev) || 0;
        developerTimeMap.set(dev, currentTime + timePerDeveloper);
      });
    }
  });

  if (developerTimeMap.size === 0) {
    return "No developer data available";
  }
  const timeFormatter = new TimeFormatter();

  const sortedDeveloperTimeEntries = Array.from(
    developerTimeMap.entries()
  ).sort(([, timeA], [, timeB]) => timeB - timeA);

  return sortedDeveloperTimeEntries
    .map(
      ([developer, time]) =>
        `${developer}: ${timeFormatter.toHoursAndMinutes(time)}`
    )
    .join(useCommaAndSpace ? ", " : "\n");
};

const mapTaskReports = (
  taskReports: MilestoneTaskReport[]
): MilestoneTask[] => {
  const taskReportsByMilestoneTaskId = new Map<number, MilestoneTaskReport[]>();

  taskReports.forEach((report) => {
    if (report.milestoneTask && report.milestoneTask.id) {
      const taskId = report.milestoneTask.id;
      if (!taskReportsByMilestoneTaskId.has(taskId)) {
        taskReportsByMilestoneTaskId.set(taskId, []);
      }
      taskReportsByMilestoneTaskId.get(taskId)?.push(report);
    } else {
      const generatedId = -(taskReportsByMilestoneTaskId.size + 1);
      taskReportsByMilestoneTaskId.set(generatedId, [report]);
    }
  });

  const milestoneTasks: MilestoneTask[] = [];

  taskReportsByMilestoneTaskId.forEach((reports, taskId) => {
    const firstReport = reports[0];
    const existingTask = firstReport.milestoneTask;

    const milestoneTask: MilestoneTask = existingTask
      ? {
          ...existingTask,
          taskReports: reports,
          timeEntries: existingTask.timeEntries,
        }
      : {
          id: taskId,
          gitLabTaskNumber: "N/A",
          taskTitle: "N/A",
          timeEstimate: "N/A",
          milestoneId: firstReport.milestoneId,
          dateCreated: firstReport.dateCreated,
          dateModified: firstReport.dateModified,
          taskReports: reports,
          timeEntries: firstReport.milestoneTask?.timeEntries || [],
        };

    milestoneTasks.push(milestoneTask);
  });

  return milestoneTasks;
};

export const calculateBudgetaryPerformance = (task: MilestoneTask): number => {
  const totalTimeSpent = task.taskReports.reduce(
    (sum, report) => sum + report.totalTimeSpentOnTask,
    0
  );

  if (task.timeEstimate === "N/A") {
    return 0;
  }

  const taskBudget =
    typeof task.timeEstimate === "number" ? task.timeEstimate : 0;

  const percentComplete = getLatestPercentageComplete(task) / 100;

  if (taskBudget <= 0 || percentComplete <= 0) {
    return 0;
  }

  return totalTimeSpent / (taskBudget * percentComplete);
};

export const calculateBudgetaryPerformanceWithoutPercentage = (
  task: MilestoneTask
): number => {
  const totalTimeSpent = task.taskReports.reduce(
    (sum, report) => sum + report.totalTimeSpentOnTask,
    0
  );

  if (task.timeEstimate === "N/A") {
    return 0;
  }

  const taskBudget =
    typeof task.timeEstimate === "number" ? task.timeEstimate : 0;

  if (taskBudget <= 0) {
    return 0;
  }

  return totalTimeSpent / taskBudget;
};

export const getLatestPercentageComplete = (task: MilestoneTask) => {
  if (!task.taskReports || task.taskReports.length === 0) {
    return 0;
  }

  if (task.taskReports.length === 1) {
    return task.taskReports[0].lastPercentageComplete;
  }

  const latestReport = task.taskReports.reduce((latest, current) => {
    const latestDate = new Date(latest.dateEnd);
    const currentDate = new Date(current.dateEnd);
    return currentDate > latestDate ? current : latest;
  }, task.taskReports[0]);

  return latestReport.lastPercentageComplete;
};

export const getLastDateEnd = (task: MilestoneTask) => {
  if (task.taskReports.length === 0) return 0;

  return Math.max(
    ...task.taskReports.map((report) =>
      report.dateEnd ? new Date(report.dateEnd).getTime() : 0
    )
  );
};

const updateTotalPages = (state: TaskReportsState) => {
  state.pagination.totalPages = Math.ceil(
    state.pagination.totalItems / state.pagination.pageSize
  );
};

export const taskReportsSlice = createSlice({
  name: "taskReports",
  initialState,
  reducers: {
    setInitializing: (state) => {
      state.initializationStatus = "initializing";
    },
    setInitialized: (state) => {
      state.initializationStatus = "initialized";
    },
    setPage: (state, action: PayloadAction<number>) => {
      const maxPage = Math.max(1, state.pagination.totalPages);
      state.pagination.currentPage = Math.min(
        maxPage,
        Math.max(1, action.payload)
      );
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
      updateTotalPages(state);
    },
    setSortConfig: (state, action: PayloadAction<SortConfig>) => {
      state.sortConfig = action.payload;
      state.sortApplied = true;
    },
    sortTaskReports: (state) => {
      const { field, direction } = state.sortConfig;
      state.milestoneTask.sort((a, b) => {
        let valueA: any;
        let valueB: any;

        switch (field) {
          case "gitLabTaskNumber":
            valueA =
              a.gitLabTaskNumber === "N/A" ? -1 : a.gitLabTaskNumber || 0;
            valueB =
              b.gitLabTaskNumber === "N/A" ? -1 : b.gitLabTaskNumber || 0;
            break;
          case "taskTitle":
            valueA = a.taskTitle === "N/A" ? "" : a.taskTitle || "";
            valueB = b.taskTitle === "N/A" ? "" : b.taskTitle || "";
            break;
          case "timeEstimate":
            valueA = a.timeEstimate === "N/A" ? -1 : a.timeEstimate || 0;
            valueB = b.timeEstimate === "N/A" ? -1 : b.timeEstimate || 0;
            break;
          case "developerNames":
            const devTimeMapA = getDeveloperTimeMap(a);
            const devTimeMapB = getDeveloperTimeMap(b);

            const sortedDevsA = Array.from(devTimeMapA.entries()).sort(
              ([, timeA], [, timeB]) => timeB - timeA
            );
            const sortedDevsB = Array.from(devTimeMapB.entries()).sort(
              ([, timeA], [, timeB]) => timeB - timeA
            );

            if (sortedDevsA.length > 0 && sortedDevsB.length > 0) {
              const topDevA = sortedDevsA[0][0];
              const topDevB = sortedDevsB[0][0];

              const nameComparison = topDevA.localeCompare(topDevB);
              if (nameComparison !== 0) {
                return direction === "asc" ? nameComparison : -nameComparison;
              }
            }

            const totalTimeA = a.taskReports.reduce(
              (sum, report) => sum + report.totalTimeSpentOnTask,
              0
            );
            const totalTimeB = b.taskReports.reduce(
              (sum, report) => sum + report.totalTimeSpentOnTask,
              0
            );

            return direction === "asc"
              ? totalTimeA - totalTimeB
              : totalTimeB - totalTimeA;
          case "typeOfWork":
            const typeOfWorkMapA = getTypeOfWorkMap(a);
            const typeOfWorkMapB = getTypeOfWorkMap(b);

            const sortedTypesA = Array.from(typeOfWorkMapA.entries()).sort(
              ([, timeA], [, timeB]) => timeB - timeA
            );
            const sortedTypesB = Array.from(typeOfWorkMapB.entries()).sort(
              ([, timeA], [, timeB]) => timeB - timeA
            );

            if (sortedTypesA.length > 0 && sortedTypesB.length > 0) {
              const topTypeA = sortedTypesA[0][0];
              const topTypeB = sortedTypesB[0][0];

              const typeComparison = topTypeA.localeCompare(topTypeB);
              if (typeComparison !== 0) {
                return direction === "asc" ? typeComparison : -typeComparison;
              }
            }

            const totalTimeTypeA = Array.from(typeOfWorkMapA.values()).reduce(
              (sum, time) => sum + time,
              0
            );
            const totalTimeTypeB = Array.from(typeOfWorkMapB.values()).reduce(
              (sum, time) => sum + time,
              0
            );

            return direction === "asc"
              ? totalTimeTypeA - totalTimeTypeB
              : totalTimeTypeB - totalTimeTypeA;
          case "totalTimeSpentOnTask":
            valueA = a.taskReports.reduce(
              (sum, report) => sum + report.totalTimeSpentOnTask,
              0
            );
            valueB = b.taskReports.reduce(
              (sum, report) => sum + report.totalTimeSpentOnTask,
              0
            );
            break;
          case "budgetaryPerformance":
            valueA = calculateBudgetaryPerformance(a);
            valueB = calculateBudgetaryPerformance(b);
            break;
          case "percentageComplete":
            const latestReportA =
              a.taskReports.length > 0
                ? a.taskReports.reduce((latest, current) => {
                    const latestDate = new Date(latest.dateEnd);
                    const currentDate = new Date(current.dateEnd);
                    return currentDate > latestDate ? current : latest;
                  }, a.taskReports[0])
                : null;

            const latestReportB =
              b.taskReports.length > 0
                ? b.taskReports.reduce((latest, current) => {
                    const latestDate = new Date(latest.dateEnd);
                    const currentDate = new Date(current.dateEnd);
                    return currentDate > latestDate ? current : latest;
                  }, b.taskReports[0])
                : null;

            valueA = latestReportA?.lastPercentageComplete || 0;
            valueB = latestReportB?.lastPercentageComplete || 0;
            break;
          case "dateRange":
            valueA = getLastDateEnd(a);
            valueB = getLastDateEnd(b);
            break;
          default:
            valueA = (a as any)[field] || 0;
            valueB = (b as any)[field] || 0;
        }

        if (typeof valueA === "string" && typeof valueB === "string") {
          return direction === "asc"
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        } else {
          return direction === "asc" ? valueA - valueB : valueB - valueA;
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaskReportsAsync.pending, (state) => {
        state.status = "loading";
        if (state.initializationStatus === "not-initialized") {
          state.initializationStatus = "initializing";
        }
      })
      .addCase(
        fetchTaskReportsAsync.fulfilled,
        (state, action: PayloadAction<MilestoneTaskReport[]>) => {
          state.status = "idle";
          state.milestoneTask = mapTaskReports(action.payload);
          state.pagination.totalItems = state.milestoneTask.length;
          updateTotalPages(state);

          if (state.initializationStatus === "initializing") {
            state.initializationStatus = "initialized";
          }

          if (state.sortApplied) {
            taskReportsSlice.caseReducers.sortTaskReports(state);
          }
        }
      )
      .addCase(fetchTaskReportsAsync.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const selectMilestoneTasks = (state: RootState) =>
  state.taskReports.milestoneTask;
export const selectTaskReportsLoading = (state: RootState) =>
  state.taskReports.status === "loading";
export const selectTaskReportsPagination = (state: RootState) =>
  state.taskReports.pagination;
export const selectTaskReportsInitializationStatus = (state: RootState) =>
  state.taskReports.initializationStatus;
export const selectTaskReportsSortConfig = (state: RootState) =>
  state.taskReports.sortConfig;
export const selectTaskReportsSortApplied = (state: RootState) =>
  state.taskReports.sortApplied;
export const selectTaskReportBudgetaryPerformance =
  (taskId: number) => (state: RootState) => {
    const task = state.taskReports.milestoneTask.find(
      (task) => task.id === taskId
    );
    return task ? calculateBudgetaryPerformance(task) : 0;
  };
export const selectTotalItems = (state: RootState) =>
  state.taskReports.milestoneTask.length;

export const {
  setInitializing,
  setInitialized,
  setPage,
  setPageSize,
  setSortConfig,
  sortTaskReports,
} = taskReportsSlice.actions;
export default taskReportsSlice.reducer;

export const getDeveloperTimeMap = (task: MilestoneTask) => {
  const developerTimeMap = new Map<string, number>();

  if (!task.taskReports || task.taskReports.length === 0) {
    return developerTimeMap;
  }

  task.taskReports.forEach((report) => {
    if (report.developerName) {
      let developers: string[] = [];

      try {
        const parsedDevelopers = JSON.parse(report.developerName);
        if (Array.isArray(parsedDevelopers)) {
          developers = parsedDevelopers;
        } else {
          developers = [report.developerName];
        }
      } catch (error) {
        developers = [report.developerName];
      }

      const timePerDeveloper = report.totalTimeSpentOnTask / developers.length;

      developers.forEach((dev) => {
        const currentTime = developerTimeMap.get(dev) || 0;
        developerTimeMap.set(dev, currentTime + timePerDeveloper);
      });
    }
  });

  return developerTimeMap;
};

export const getTypeOfWorkMap = (task: MilestoneTask) => {
  const typeOfWorkMap = new Map<string, number>();

  if (!task.timeEntries || task.timeEntries.length === 0) {
    return typeOfWorkMap;
  }

  task.timeEntries.forEach((entry) => {
    if (entry.typeOfWork) {
      const currentTime = typeOfWorkMap.get(entry.typeOfWork) || 0;
      typeOfWorkMap.set(entry.typeOfWork, currentTime + entry.timeSpentInHours);
    }
  });

  return typeOfWorkMap;
};

export const formatTypeOfWork = (task: MilestoneTask): string => {
  if (!task.timeEntries || task.timeEntries.length === 0) {
    return "N/A";
  }

  const typeOfWorkMap = getTypeOfWorkMap(task);
  
  if (typeOfWorkMap.size === 0) {
    return "N/A";
  }

  const sortedTypes = Array.from(typeOfWorkMap.entries())
    .sort(([, timeA], [, timeB]) => timeB - timeA)
    .map(([type]) => type);

  return sortedTypes.join(", ");
};

export const getTypeOfWorkBreakdown = (task: MilestoneTask): string => {
  if (!task.timeEntries || task.timeEntries.length === 0) {
    return "N/A";
  }

  const typeOfWorkMap = getTypeOfWorkMap(task);
  
  if (typeOfWorkMap.size === 0) {
    return "N/A";
  }

  const sortedTypes = Array.from(typeOfWorkMap.entries())
    .sort(([, timeA], [, timeB]) => timeB - timeA);

  const timeFormatter = new TimeFormatter();
  
  return sortedTypes
    .map(([type, time]) => `${type}: ${timeFormatter.toHoursAndMinutes(time)}`)
    .join("\n");
};
